import ResponsiveTable from "@/components/ResponsiveTable";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CUSTOMER_SOURCES, CUSTOMER_STATUSES } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Phone, Mail, Bell, AlertTriangle, CalendarClock, Send, CalendarCheck, UserPlus } from "lucide-react";

const quickFilters = [
  { key: "overdue", label: "Geciken Takip", icon: AlertTriangle, color: "#EF4444", patch: { followup: "overdue", status: "" }, stat: "overdue_followups" },
  { key: "today", label: "Bugünkü Takip", icon: Bell, color: "#F59E0B", patch: { followup: "today", status: "" }, stat: "today_followups" },
  { key: "upcoming", label: "Yaklaşan Takip", icon: CalendarClock, color: "#3B82F6", patch: { followup: "upcoming", status: "" }, stat: "upcoming_followups" },
  { key: "offer", label: "Teklif Verilen", icon: Send, color: "#8B5CF6", patch: { followup: "", status: "Teklif Verildi" }, stat: "offer_customers" },
  { key: "appointment", label: "Randevulu", icon: CalendarCheck, color: "#10B981", patch: { followup: "", status: "Randevu Alındı" }, stat: "appointment_customers" },
  { key: "new", label: "Yeni", icon: UserPlus, color: "#0F172A", patch: { followup: "", status: "Yeni" }, stat: "new_customers" },
];

export default function Customers() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    status: searchParams.get("status") || "",
    followup: searchParams.get("followup") || "",
  });
  const urlStatus = searchParams.get("status") || "";
  const urlFollowup = searchParams.get("followup") || "";
  const [showForm, setShowForm] = useState(false);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [unitsForInterest, setUnitsForInterest] = useState([]);
  const [form, setForm] = useState({
    full_name: "", phone: "", email: "", source: "Telefon", status: "Yeni",
    budget_min: 0, budget_max: 0, currency: "TRY", assigned_agent_id: "",
    next_followup_at: "", general_note: "",
    interest_project_id: "", interest_unit_id: "",
    interest_status: "İlgileniyor", first_note: "",
  });

  const load = useCallback(async () => {
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.followup) params.followup = filters.followup;
    const { data } = await api.get("/customers", { params });
    setCustomers(data);
  }, [filters]);
  const loadSummary = async () => {
    const { data } = await api.get("/customers/summary");
    setSummary(data);
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    setFilters({
      status: urlStatus,
      followup: urlFollowup,
    });
  }, [urlStatus, urlFollowup]);
  useEffect(() => {
    loadSummary();
    api.get("/company/users").then(({ data }) => setUsers(data));
    api.get("/projects").then(({ data }) => setProjects(data));
  }, []);
  useEffect(() => {
    if (form.interest_project_id) {
      api.get("/units", { params: { project_id: form.interest_project_id } }).then(({ data }) => setUnitsForInterest(data));
    } else {
      setUnitsForInterest([]);
    }
  }, [form.interest_project_id]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, budget_min: Number(form.budget_min) || 0, budget_max: Number(form.budget_max) || 0 };
      if (!payload.assigned_agent_id) delete payload.assigned_agent_id;
      if (!payload.interest_project_id) delete payload.interest_project_id;
      if (!payload.interest_unit_id) delete payload.interest_unit_id;
      await api.post("/customers", payload);
      toast.success("Müşteri eklendi");
      setShowForm(false); load(); loadSummary();
      setForm({ full_name: "", phone: "", email: "", source: "Telefon", status: "Yeni",
                budget_min: 0, budget_max: 0, currency: "TRY", assigned_agent_id: "",
                next_followup_at: "", general_note: "",
                interest_project_id: "", interest_unit_id: "", interest_status: "İlgileniyor", first_note: "" });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const canAssign = user?.role !== "agent";
  const hasActiveFilter = filters.status || filters.followup || search;
  const visibleCustomers = customers.filter(c => [c.full_name, c.phone, c.email].filter(Boolean).join(" ").toLocaleLowerCase("tr").includes(search.trim().toLocaleLowerCase("tr")));
  const today = new Date().toISOString().slice(0, 10);
  const followupClass = (value) => {
    const date = (value || "").slice(0, 10);
    if (!date) return "text-slate-500";
    if (date < today) return "text-red-600 font-semibold";
    if (date === today) return "text-amber-700 font-semibold";
    return "text-slate-500";
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-display">Müşteriler</h1>
          <p className="text-slate-500 mt-1">{visibleCustomers.length} müşteri</p>
        </div>
        <button onClick={() => setShowForm(true)}
                className="bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg px-4 py-2 inline-flex items-center gap-2 transition"
                data-testid="new-customer-btn">
          <Plus className="w-4 h-4" /> Yeni Müşteri
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {quickFilters.map((item) => {
          const Icon = item.icon;
          const active = Object.entries(item.patch).every(([k, v]) => (filters[k] || "") === v);
          return (
            <button key={item.key} type="button"
                    onClick={() => setFilters({ ...filters, ...item.patch })}
                    className={`bg-white border rounded-xl p-4 text-left transition ${active ? "border-amber-400 ring-2 ring-amber-100" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid={`quick-filter-${item.key}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{item.label}</div>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${item.color}18`, color: item.color }}>
                  <Icon className="w-4 h-4" />
                </div>
              </div>
              <div className="mt-2 text-2xl font-bold text-slate-900 font-display">{summary?.[item.stat] ?? 0}</div>
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <input type="search" aria-label="Müşteri ara" placeholder="Ad, telefon veya e-posta" className="input sm:max-w-xs" value={search} onChange={e => setSearch(e.target.value)} />
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="input w-auto" data-testid="filter-customer-status">
          <option value="">Tüm Durumlar</option>
          {CUSTOMER_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select value={filters.followup} onChange={(e) => setFilters({ ...filters, followup: e.target.value })}
                className="input w-auto" data-testid="filter-customer-followup">
          <option value="">Tüm Takipler</option>
          <option value="overdue">Geciken Takipler</option>
          <option value="today">Bugünkü Takipler</option>
          <option value="upcoming">Yaklaşan Takipler</option>
        </select>
        {hasActiveFilter && (
          <button type="button"
                  onClick={() => { setFilters({ status: "", followup: "" }); setSearch(""); }}
                  className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg hover:border-slate-300">
            Filtreleri Temizle
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-6 space-y-4" data-testid="customer-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <F label="Ad Soyad" required><input required className="input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="customer-name" /></F>
            <F label="Telefon"><input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="customer-phone" /></F>
            <F label="E-posta"><input type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></F>
            <F label="Kaynak">
              <select className="input" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
                {CUSTOMER_SOURCES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </F>
            <F label="Durum">
              <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {CUSTOMER_STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </F>
            {canAssign && (
              <F label="Atanan Personel">
                <select className="input" value={form.assigned_agent_id} onChange={(e) => setForm({ ...form, assigned_agent_id: e.target.value })} data-testid="customer-assign">
                  <option value="">Seçin</option>
                  {users.filter((u) => u.role !== "admin").map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </F>
            )}
            <F label="Bütçe Min"><input type="number" className="input" value={form.budget_min} onChange={(e) => setForm({ ...form, budget_min: e.target.value })} /></F>
            <F label="Bütçe Max"><input type="number" className="input" value={form.budget_max} onChange={(e) => setForm({ ...form, budget_max: e.target.value })} /></F>
            <F label="Sonraki Takip"><input type="date" className="input" value={form.next_followup_at?.slice(0, 10) || ""} onChange={(e) => setForm({ ...form, next_followup_at: e.target.value })} /></F>
            <div className="md:col-span-2">
              <F label="Genel Not"><textarea rows={2} className="input resize-none" value={form.general_note} onChange={(e) => setForm({ ...form, general_note: e.target.value })} /></F>
            </div>
            <div className="md:col-span-2 mt-2 pt-3 border-t border-slate-200">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-700">İlgilendiği Proje / Bölüm (opsiyonel)</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Doldurulursa otomatik "İlgi Kaydı" oluşturulur ve ilk görüşme notu eklenir.</div>
            </div>
            <F label="İlgilendiği Proje">
              <select className="input" value={form.interest_project_id}
                      onChange={(e) => setForm({ ...form, interest_project_id: e.target.value, interest_unit_id: "" })}
                      data-testid="customer-interest-project">
                <option value="">Seçin</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </F>
            <F label="İlgilendiği Bölüm">
              <select className="input" value={form.interest_unit_id}
                      onChange={(e) => setForm({ ...form, interest_unit_id: e.target.value })}
                      disabled={!form.interest_project_id}
                      data-testid="customer-interest-unit">
                <option value="">Seçin</option>
                {unitsForInterest.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.block_name || "Blok yok"} / {u.floor_name || "Kat yok"} / {u.unit_no} — {u.room_type} ({u.status})
                  </option>
                ))}
              </select>
            </F>
            <F label="İlgi Durumu">
              <select className="input" value={form.interest_status} onChange={(e) => setForm({ ...form, interest_status: e.target.value })}>
                {["İlgileniyor", "Plan Gösterildi", "Fiyat Verildi", "Randevu Verildi"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </F>
            <div className="md:col-span-2">
              <F label="İlk Görüşme Notu"><textarea rows={2} className="input resize-none" value={form.first_note}
                                                     onChange={(e) => setForm({ ...form, first_note: e.target.value })}
                                                     placeholder="Müşteriye bütçesine uygun yerler gösterildi…" /></F>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-slate-200 rounded-lg">Vazgeç</button>
            <button type="submit" className="px-4 py-2 text-sm bg-slate-900 text-white rounded-lg" data-testid="submit-customer">Kaydet</button>
          </div>
        </form>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden overflow-x-auto">
        <ResponsiveTable className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Ad Soyad</th>
              <th className="text-left px-4 py-3">İletişim</th>
              <th className="text-left px-4 py-3">Kaynak</th>
              <th className="text-left px-4 py-3">Durum</th>
              <th className="text-left px-4 py-3">Personel</th>
              <th className="text-left px-4 py-3">Takip</th>
              <th className="text-right px-4 py-3">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {visibleCustomers.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`customer-row-${c.id}`}>
                <td className="px-4 py-3">
                  <Link to={`/customers/${c.id}`} className="font-medium text-slate-900 hover:text-amber-600">{c.full_name}</Link>
                </td>
                <td className="px-4 py-3 text-xs">
                  {c.phone && <a href={`tel:${c.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-1 text-slate-600 hover:text-amber-700"><Phone className="w-3 h-3" /> {c.phone}</a>}
                  {c.email && <div className="inline-flex items-center gap-1 text-slate-600"><Mail className="w-3 h-3" /> {c.email}</div>}
                </td>
                <td className="px-4 py-3">{c.source}</td>
                <td className="px-4 py-3">{c.status}</td>
                <td className="px-4 py-3 text-slate-600">{c.assigned_agent_name || "—"}</td>
                <td className={`px-4 py-3 text-xs ${followupClass(c.next_followup_at)}`}>{(c.next_followup_at || "").slice(0, 10) || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/customers/${c.id}?note=1`} className="text-xs font-semibold text-amber-700 hover:text-amber-800 whitespace-nowrap">
                    Görüşme notu ekle
                  </Link>
                </td>
              </tr>
            ))}
            {visibleCustomers.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">Müşteri yok</td></tr>
            )}
          </tbody>
        </ResponsiveTable>
      </div>
    </div>
  );
}

function F({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">{label}{required && <span className="text-red-500"> *</span>}</div>
      {children}
    </label>
  );
}
